import {
  calculateEloChange,
  calculateKFactor,
  ratingCertainty,
  ELO_BASE_K_FACTOR,
  ELO_POLICY_CURRENT,
  ELO_POLICY_LEGACY,
} from './eloUtils';
import {
  getScorelineNudge,
  getCompetitiveResultScore,
  clampCompetitiveEloDelta,
} from './gameRatingUtils';
import { getSinglesEloForComputation } from './playerUtils';

/**
 * Head-to-head singles Elo after one game. Aligns with sessionSimulationEngine.calculateSinglesRatedEloChanges.
 * Types 4 & 5: rated; type 7: no rating change (returns current computed singles baseline).
 *
 * @returns {Record<string, { newRating: number, delta: number, baseDelta: number, nudge: number }>}
 */
export function computeSinglesEloResults(team1Player, team2Player, {
  team1Won,
  team1Score,
  team2Score,
  gameTypeId,
  eloPolicy = ELO_POLICY_CURRENT,
}) {
  const p1 = team1Player;
  const p2 = team2Player;
  if (!p1?.id || !p2?.id) return {};

  const ratingOf = (p) => getSinglesEloForComputation(p);
  const gamesOf = (p) => Number(p.singles_games_played) || 0;

  if (gameTypeId === 7) {
    return {
      [p1.id]: { newRating: ratingOf(p1), delta: 0, baseDelta: 0, nudge: 0 },
      [p2.id]: { newRating: ratingOf(p2), delta: 0, baseDelta: 0, nudge: 0 },
    };
  }

  const useLegacy = eloPolicy === ELO_POLICY_LEGACY;
  const r1 = ratingOf(p1);
  const r2 = ratingOf(p2);
  let team1ActualScore;
  let team2ActualScore;
  let allowLoserGain = false;
  let team1Nudge = 0;
  let team2Nudge = 0;

  if (useLegacy) {
    const margin = Math.abs((Number(team1Score) || 0) - (Number(team2Score) || 0));
    const balanced = gameTypeId === 4 || gameTypeId === 5;
    const scorelineNudge = balanced ? getScorelineNudge(margin) : 0;
    team1ActualScore = team1Won ? 1 : 0;
    team2ActualScore = team1Won ? 0 : 1;
    team1Nudge = team1Won ? scorelineNudge : -scorelineNudge;
    team2Nudge = -team1Nudge;
  } else {
    const result = getCompetitiveResultScore(team1Score, team2Score);
    allowLoserGain = result.allowLoserGain;
    team1ActualScore = team1Won ? result.winnerScore : 1 - result.winnerScore;
    team2ActualScore = 1 - team1ActualScore;
  }

  const team1BaseDelta = calculateEloChange(r1, r2, team1ActualScore, ELO_BASE_K_FACTOR, 1);
  const team2BaseDelta = calculateEloChange(r2, r1, team2ActualScore, ELO_BASE_K_FACTOR, 1);

  const apply = (player, opponent, baseDelta, nudge, won) => {
    const playerGames = gamesOf(player);
    const playerKFactor = calculateKFactor(playerGames, false, eloPolicy);
    const certainty = useLegacy ? 1 : ratingCertainty(gamesOf(opponent), eloPolicy);
    let playerDelta = Math.round(
      baseDelta * (playerKFactor / ELO_BASE_K_FACTOR) * certainty
    );
    const appliedNudge = useLegacy ? nudge : 0;
    if (!useLegacy) {
      playerDelta = clampCompetitiveEloDelta(playerDelta, { won, allowLoserGain });
    }
    const currentRating = ratingOf(player);
    const total = playerDelta + appliedNudge;
    return {
      newRating: currentRating + total,
      delta: total,
      baseDelta: playerDelta,
      nudge: appliedNudge,
    };
  };

  return {
    [p1.id]: apply(p1, p2, team1BaseDelta, team1Nudge, team1Won),
    [p2.id]: apply(p2, p1, team2BaseDelta, team2Nudge, !team1Won),
  };
}
